import type {
  SystemPrompt,
  SystemPromptFilter,
  SystemPromptStore,
} from '../../ports/system-prompts'
import { BackendError } from '../../errors'
import { delay, uid } from './util'

/** Seed system prompts, also used to bootstrap the server-side store. */
export const SYSTEM_PROMPT_SEED: SystemPrompt[] = [
  {
    id: 'sys_technische_redaktion',
    name: 'Technische Redaktion',
    version: 1,
    active: true,
    description: 'Standard-Assistent für die technische Dokumentation in Word.',
    body:
      'Du bist der SafeAI-Plus-Assistent für die technische Redaktion in Microsoft Word. Du unterstützt Redakteurinnen und Redakteure dabei, technische Dokumentation klar, konsistent und normgerecht zu erstellen und zu überarbeiten.\n\n' +
      'So arbeitest du:\n' +
      '- Antworte auf Deutsch, sachlich und präzise. Fasse dich so kurz wie möglich, ohne Inhalt zu verlieren.\n' +
      '- Stütze dich ausschließlich auf den bereitgestellten Kontext (die Auswahl oder das Dokument). Erfinde keine Inhalte, Messwerte, Fristen oder Bedingungen. Fehlt eine nötige Information, setze einen Platzhalter in eckigen Klammern, der benennt, was fehlt – zum Beispiel „[Folgen der Gefahr nennen]".\n' +
      '- Formuliere Handlungsaufforderungen im imperativischen Infinitiv, zum Beispiel „Die Befestigungsschrauben lösen.". Nenne das Handlungsobjekt mit Artikel.\n' +
      '- Strukturiere Inhalte nachvollziehbar: sinnvolle Zwischenüberschriften, technische Daten als Tabelle, Handlungen getrennt von erläuterndem Text.\n' +
      '- Schreibe verständlich, einheitlich und geschlechtsneutral. Vermeide Füllwörter, Werbesprache und unnötige Wiederholungen.\n' +
      '- Gib standardmäßig nur das überarbeitete Ergebnis aus. Erkläre dein Vorgehen nur, wenn ausdrücklich danach gefragt wird.',
    locale: 'de',
    host: 'word',
    source: 'local',
  },
  {
    id: 'sys_neutral',
    name: 'Neutraler Assistent',
    version: 1,
    description: 'Allgemeiner, zurückhaltender Assistent ohne redaktionelle Vorgaben.',
    body:
      'Du bist ein hilfreicher, zurückhaltender Assistent in Microsoft Word. Du beantwortest Fragen und bearbeitest Texte auf Grundlage des bereitgestellten Kontexts.\n\n' +
      'So arbeitest du:\n' +
      '- Antworte in der Sprache der Nutzerin oder des Nutzers, standardmäßig auf Deutsch.\n' +
      '- Beziehe dich auf den bereitgestellten Kontext und erfinde keine Fakten. Wenn du etwas nicht weißt, sage es.\n' +
      '- Fasse dich klar und so kurz wie möglich.\n' +
      '- Mache keine ungefragten Vorgaben zu Stil oder Struktur; setze um, was verlangt wird.',
    locale: 'de',
    host: 'all',
    source: 'local',
  },
]

export class MockSystemPromptStore implements SystemPromptStore {
  private prompts: SystemPrompt[] = SYSTEM_PROMPT_SEED.map((p) => ({ ...p }))

  constructor(private readonly latency: number) {}

  async list(filter?: SystemPromptFilter): Promise<SystemPrompt[]> {
    await delay(this.latency)
    let out = [...this.prompts]
    if (filter?.source) out = out.filter((p) => p.source === filter.source)
    if (filter?.host) out = out.filter((p) => p.host === filter.host || p.host === 'all')
    if (filter?.locale) out = out.filter((p) => !p.locale || p.locale === filter.locale)
    if (filter?.search) {
      const q = filter.search.toLowerCase()
      out = out.filter((p) => p.name.toLowerCase().includes(q))
    }
    return out
  }

  async get(id: string): Promise<SystemPrompt> {
    await delay(this.latency)
    const p = this.prompts.find((x) => x.id === id)
    if (!p) throw new BackendError({ code: 'not_found', message: `System prompt ${id} not found` })
    return p
  }

  async getActive(): Promise<SystemPrompt | null> {
    await delay(this.latency)
    return this.prompts.find((p) => p.active) ?? null
  }

  async setActive(id: string): Promise<SystemPrompt> {
    const target = await this.get(id)
    for (const p of this.prompts) p.active = p.id === id
    target.updatedAt = new Date().toISOString()
    return target
  }

  async create(input: Omit<SystemPrompt, 'id' | 'version' | 'updatedAt'>): Promise<SystemPrompt> {
    await delay(this.latency)
    const p: SystemPrompt = {
      ...input,
      id: uid('sys'),
      version: 1,
      updatedAt: new Date().toISOString(),
    }
    if (p.active) for (const other of this.prompts) other.active = false
    this.prompts.push(p)
    return p
  }

  async update(id: string, patch: Partial<SystemPrompt>): Promise<SystemPrompt> {
    const p = await this.get(id)
    if (patch.active) for (const other of this.prompts) other.active = false
    Object.assign(p, patch, { version: p.version + 1, updatedAt: new Date().toISOString() })
    return p
  }

  async delete(id: string): Promise<void> {
    await delay(this.latency)
    this.prompts = this.prompts.filter((x) => x.id !== id)
  }
}
